import React, { useState, useEffect } from "react";
import { useDrag } from "react-dnd";
import {
    SearchIcon,
    SearchShortcutIconWin,
    SearchShortcutIconMac,
} from "../../../../../assets/svg/templates/templateIcons";
import style from "./style.module.css";
import { ItemTypes } from "../types/ItemTypes";
import {
    SleekSingleColumn,
    BalancedTwoColumns,
    TwoColumnsWithSearch,
    ThirtySeventySplitLayout,
    SeventyThirtySplitLayout,
    GridLayout,
    DashboardLayout,
    Banner,
    PreLoginHeader,
    PostLoginHeader,
    BasicSectionHeader,
    SectionHeaderWithAction,
    ContactForm,
    LoginForm,
    AddressForm,
    ReviewCard,
    SummaryList,
    BlogCard,
    OrderSummary,
    ProfitInformation,
    LossInformation,
    ConfirmationModalForDeletion,
    SuccessModal,
    UnsuccessfulModal,
    TermsAndConditions,
    PricingTable,
    TableList,
    ComparisonTable,
    FooterBasic,
    FooterWithBlogTitle,
    FooterWithGetAppButton,
    FooterWithLinks,
    OTP,
    TechnicalIssue,
    Redirect,
    Completion,
    NoData,
    FAQ
} from "./assets/templateImgs";
import { motion, AnimatePresence } from "framer-motion";
import { OS, getOs } from "../../formBuilder/helpers/getOs";

const container = {
    hidden: { opacity: 1, scale: 0 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            delayChildren: 0.1,
            staggerChildren: 0.07,
        },
    },
};

const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,

        transition: {
            delayChildren: 0.3,
            staggerChildren: 0.2,
        },
    },
};

interface TemplateModalProps {
    isCheckedIn: boolean;
    isDragging?: boolean;
    isVisible?: boolean;
    onClose?: () => void;
    id: string;
    title: string;
    json: any;
    icon: any;
    onDrag?: any;
    handleSearch?: any;
}

const DragComponent: React.FC<TemplateModalProps> = ({
    id,
    title,
    json,
    icon,
    isCheckedIn,
    onDrag,
    handleSearch,
}) => {
    const [{ isDragging }, drag] = useDrag({
        item: { type: ItemTypes.TEMPLATE, json },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        canDrag: isCheckedIn,
    });

    useEffect(() => {
        if (isDragging) onDrag(isDragging);
    }, [isDragging, onDrag]);

    return (
        <motion.div
            variants={item}
            key={id}
            ref={drag}
            className="col-span-4 text-center"
        >
            <div style={{ padding: "11px 11px 0px 0px" }}>
                <div
                    className={style.imgHoverZoom}
                    style={{
                        width: "116px",
                        height: "120px",
                        borderRadius: "8px",
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                        border: "1px solid #A8ABBB61",
                        cursor: "pointer",
                        overflow: "hidden",
                    }}
                >
                    <div className={style.imgWrapper}>{icon}</div>
                </div>
                <p className={style.templateLabel}>{handleSearch(title)}</p>
            </div>
        </motion.div>
    );
};

const TemplateModal: React.FC<any> = ({ isV2, isVisible, isCheckedIn, onClose }) => {
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [templateList, setTemplateList] = useState<any>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>("Layout");
    const [searchTerm, setSearchTerm] = useState<string>("");

    const menuList = [
        {
            id: "Layout",
            title: "Layout",
            items: [
                {
                    id: "SleekSingleColumn",
                    title: "Sleek Single Column",
                    path: "/layouts/single-column-layout.json",
                    icon: <SleekSingleColumn />,
                },
                {
                    id: "BalancedTwoColumns",
                    title: "Balanced Two Columns",
                    path: "/layouts/parallel-two-column.json",
                    icon: <BalancedTwoColumns />,
                },
                {
                    id: "TwoColumnsWithSearch",
                    title: "Two Columns with Search",
                    path: "/layouts/two-column-with-search.json",
                    icon: <TwoColumnsWithSearch />,
                },
                {
                    id: "ThirtySeventySplitLayout",
                    title: "30-70 Split Layout",
                    path: "/layouts/thirty-seventy-split-layout.json",
                    icon: <ThirtySeventySplitLayout />,
                },
                {
                    id: "SeventyThirtySplitLayout",
                    title: "70-30 Split Layout",
                    path: "/layouts/seventy-thirty-split-layout.json",
                    icon: <SeventyThirtySplitLayout />,
                },
                {
                    id: "GridLayout",
                    title: "Grid View",
                    path: "/layouts/grid-layout.json",
                    icon: <GridLayout />,
                },
                {
                    id: "DashboardLayout",
                    title: "Dashboard",
                    path: "/layouts/dashboard-layout.json",
                    icon: <DashboardLayout />,
                },
            ],
        },
        {
            id: "Header",
            title: "Header",
            items: [
                {
                    id: "Banner",
                    title: "Banner",
                    path: "/header/banner.json",
                    icon: <Banner />,
                },
                {
                    id: "PreLoginHeader",
                    title: "Pre-Login",
                    path: "/header/header-before-login.json",
                    icon: <PreLoginHeader />,
                },
                {
                    id: "PostLoginHeader",
                    title: "Post-Login",
                    path: "/header/header-with-tab.json",
                    icon: <PostLoginHeader />,
                },
                {
                    id: "SectionHeaderWithAction",
                    title: "Section with Tag",
                    path: "/header/section-header-with-tags.json",
                    icon: <BasicSectionHeader />,
                },
                {
                    id: "BasicSectionHeader",
                    title: "Basic Section",
                    path: "/header/section-header-basic.json",
                    icon: <SectionHeaderWithAction />,
                },
            ],
        },
        {
            id: "Form",
            title: "Form",
            items: [
                {
                    id: "ContactForm",
                    title: "Contact Us",
                    path: "/forms/contact-form.json",
                    icon: <ContactForm />,
                },
                {
                    id: "AddressForm",
                    title: "Address",
                    path: "/forms/address-form.json",
                    icon: <AddressForm />,
                },
                {
                    id: "LoginForm",
                    title: "Login",
                    path: "/forms/login-form.json",
                    icon: <LoginForm />,
                },
                {
                    id: "otpForm",
                    title: "OTP",
                    path: "/forms/otp-form.json",
                    icon: <OTP />,
                },
            ],
        },
        {
            id: "Cards",
            title: "Cards",
            items: [
                {
                    id: "ReviewCard",
                    title: "Review",
                    path: "/cards/review-card.json",
                    icon: <ReviewCard />,
                },
                {
                    id: "ListCard",
                    title: "List",
                    path: "/cards/list-card.json",
                    icon: <SummaryList />,
                },
                {
                    id: "BlogCard",
                    title: "Blog Card",
                    path: "/cards/blog-card.json",
                    icon: <BlogCard />,
                },
                {
                    id: "OrderSummary",
                    title: "Order Summary",
                    path: "/cards/order-summary.json",
                    icon: <OrderSummary />,
                },
                {
                    id: "SummaryList",
                    title: "Summary List",
                    path: "/cards/summary-card.json",
                    icon: <SummaryList />,
                },
                {
                    id: "ProfitInformation",
                    title: "Profit Information",
                    path: "/cards/profit-information.json",
                    icon: <ProfitInformation />,
                },
                {
                    id: "LossInformation",
                    title: "Loss Information",
                    path: "/cards/loss-information.json",
                    icon: <LossInformation />,
                },
                {
                    id: "NoData",
                    title: "No Data",
                    path: "/cards/no-data.json",
                    icon: <NoData />,
                },
                {
                    id: "redirect",
                    title: "Redirect",
                    path: "/cards/redirect.json",
                    icon: <Redirect/>,
                },
                {
                    id: "completion",
                    title: "Completion",
                    path: "/cards/completion.json",
                    icon: <Completion />,
                },
                {
                    id: "technicalIssue",
                    title: "Technical Issue",
                    path: "/cards/technical-issue.json",
                    icon: <TechnicalIssue />,
                },
            ],
        },
        {
            id: "Modal",
            title: "Modal",
            items: [
                {
                    id: "ConfirmationModalForDeletion",
                    title: "Confirmation with Description",
                    path: "/modal/delete-confirmation-modal.json",
                    icon: <ConfirmationModalForDeletion />,
                },
                {
                    id: "SuccessModal",
                    title: "Success",
                    path: "/modal/success-modal.json",
                    icon: <SuccessModal />,
                },
                {
                    id: "UnsuccessfulModal",
                    title: "Unsuccessful",
                    path: "/modal/unsuccessful-modal.json",
                    icon: <UnsuccessfulModal />,
                },
                {
                    id: "TermsAndConditions",
                    title: "Terms and Conditions",
                    path: "/modal/terms-and-condition.json",
                    icon: <TermsAndConditions />,
                },
            ],
        },
        {
            id: "Table",
            title: "Table",
            items: [
                {
                    id: "PricingTable",
                    title: "Pricing",
                    path: "/table/pricing-table.json",
                    icon: <PricingTable />,
                },
                {
                    id: "TableList",
                    title: "Table List",
                    path: "/table/table-list.json",
                    icon: <TableList />,
                },
                {
                    id: "ComparisonTable",
                    title: "Comparison",
                    path: "/table/comparison-table.json",
                    icon: <ComparisonTable />,
                },
                {
                    id: "FAQList",
                    title: "FAQ List",
                    path: "/table/faq-list.json",
                    icon: <FAQ />,
                },
            ],
        },
        {
            id: "Footer",
            title: "Footer",
            items: [
                {
                    id: "FooterBasic",
                    title: "Footer Basic",
                    path: "/footer/footer-basic.json",
                    icon: <FooterBasic />,
                },
                {
                    id: "FooterWithBlogTitle",
                    title: "Footer with Blog Title",
                    path: "/footer/footer-with-blog-title.json",
                    icon: <FooterWithBlogTitle />,
                },
                {
                    id: "FooterWithGetAppButton",
                    title: "Footer With Get App Button",
                    path: "/footer/footer-with-get-app-button.json",
                    icon: <FooterWithGetAppButton />,
                },
                {
                    id: "FooterWithLinks",
                    title: "Footer With Links",
                    path: "/footer/footer-with-links.json",
                    icon: <FooterWithLinks />,
                },
            ],
        },
    ];

    useEffect(() => {
        const categoryItems =
            menuList.find((data) => data.id === selectedCategory)?.items || [];
        setTemplateList(categoryItems);
    }, [selectedCategory]);

    const onSearchChange = (searchValue: string) => {
        setSearchTerm(searchValue)
        if (searchValue) {
            const foundCategory = menuList.find(
                (item) =>
                    item.title
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    item.items.some((subItem) =>
                        subItem.title
                            .toLowerCase()
                            .includes(searchValue.toLowerCase())
                    )
            );

            if (foundCategory) {
                setSelectedCategory(foundCategory.id);
            } else {
                setSelectedCategory("Layout");
            }
        } else {
            setSelectedCategory("Layout");
        }
    }

    const filteredTemplateList = templateList.filter((item: any) =>{
        if (selectedCategory && selectedCategory.toLowerCase().includes(searchTerm?.toLowerCase())) {
            return item
        } else {
            return item?.title?.toLowerCase().includes(searchTerm?.toLowerCase());
        }
    });

    const handleSearch = (label: string) => {
        if (!searchTerm) return <>{label}</>;
        try {
            const regex = new RegExp(`(${searchTerm})`, "gi");
            const parts = label.split(regex);
            return (
                <>
                    {parts.map((part, index) =>
                        regex.test(part) ? <b key={index}>{part}</b> : part
                    )}
                </>
            );
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        setIsDragging(false);
        setSelectedCategory("Layout");
        setSearchTerm("")
    }, [isVisible]);

    const os = getOs();

    return (
        !isV2 &&
        isCheckedIn &&
        isVisible && (
            <>
                <div
                    onClick={onClose}
                    className={style.templateBackdrop}
                    style={{
                        display: !isDragging ? "block" : "none",
                    }}
                />
                <AnimatePresence>
                    <motion.div
                        initial={{
                            opacity: 0,
                            scale: 0.75,
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            y: 0,
                            transition: {
                                ease: "easeOut",
                                duration: 0.15,
                            },
                        }}
                        exit={{
                            opacity: 0,
                            scale: 0.75,
                            transition: {
                                ease: "easeIn",
                                duration: 0.15,
                            },
                        }}
                        className={style.templateModal}
                    >
                        <div className={style.templateModalHeader}>
                            <div
                                className="relative flex justify-between items-center"
                                style={{ width: "100%" }}
                            >
                                <div
                                    className="absolute inset-y-0 start-0 pointer-events-none"
                                    style={{ width: "18px", height: "18px" }}
                                >
                                    <SearchIcon />
                                </div>
                                <input
                                    type="search"
                                    id="search"
                                    className="block text-sm text-gray-900 border-0 focus:border-0 focus:ring-0 outline-none focus:outline-none dark:bg-white-700 dark:placeholder-gray-400 dark:text-white"
                                    placeholder="Search template..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        onSearchChange(e.target.value)
                                    }
                                    required
                                />
                                <div style={{ width: "55px", height: "21px" }}>
                                    {os == OS.Mac ? (
                                        <SearchShortcutIconMac />
                                    ) : (
                                        <SearchShortcutIconWin />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            className="relative h-full overflow-hidden flex"
                            style={{
                                height: "calc(100% - 65px)",
                            }}
                        >
                            <div
                                style={{
                                    borderRight: "1px solid #A8ABBB61",
                                    height: "100%",
                                    overflowY: "auto",
                                    width: "151px",
                                }}
                            >
                                {menuList.map((rowItem) => (
                                    <p
                                        key={rowItem.id}
                                        className={
                                            rowItem.id === selectedCategory
                                                ? style.selectedTemplateMenu
                                                : style.templateMenu
                                        }
                                        onClick={() => {
                                            setSelectedCategory(rowItem.id);
                                            setSearchTerm(""); // Clear search on category select
                                        }}
                                    >
                                        {handleSearch(rowItem.title)}
                                    </p>
                                ))}
                            </div>

                            <div
                                className="overflow-hidden gap-0"
                                style={{
                                    width: "calc(100% - 151px)",
                                    height: "calc(100% - 12px)",
                                    overflowY: "auto",
                                }}
                            >
                                <motion.div
                                    variants={container}
                                    className="relative grid grid-cols-12 gap-0"
                                    style={{ padding: "0px 0px 12px 12px" }}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {filteredTemplateList?.length > 0 ? (
                                        filteredTemplateList.map(
                                            (rowItem: any) => (
                                                <DragComponent
                                                    key={rowItem.id}
                                                    id={rowItem.id}
                                                    title={rowItem.title}
                                                    json={rowItem.path}
                                                    icon={rowItem.icon}
                                                    isCheckedIn={isCheckedIn}
                                                    onDrag={onClose}
                                                    handleSearch={handleSearch} // Pass handleSearch to DragComponent
                                                />
                                            )
                                        )
                                    ) : (
                                        <div
                                            className="col-span-12 flex justify-center items-center"
                                            style={{
                                                color: "#292d32",
                                                height: "calc(450px - 80px)",
                                            }}
                                        >
                                            The template you're looking for is not available.
                                        </div>
                                    )}
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </>
        )
    );
};

export default TemplateModal;
